import { useEffect, useState } from 'react'
import { ChevronDown } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { DecimalInput } from '@/components/ui/DecimalInput'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ApiError } from '@/lib/api/client'
import { parseDecimalInput } from '@/lib/decimal'
import { cn } from '@/lib/utils'

import { isWholeQuantity, originFacts, originSummary, vatOptionsFor } from './imported-line'
import { LinePropertiesEditor } from './LinePropertiesEditor'
import {
  newPropertyRow,
  propertiesProblem,
  propertyRows,
  propertyRowsFromDescription,
  toLineProperties,
  type PropertyRow,
} from './line-properties'
import { getProjectSalesVatsV2, updateQuoteVersionLineV2 } from './quotes-v2.api'
import type { LineResponseDto, ProjectSalesVatsDto, QuoteLineOrigin } from './types'

interface ImportedLineDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: string
  quoteId: string | null
  versionId: string | null
  lineId: string | null
  initialLine: LineResponseDto | null
  origin: QuoteLineOrigin | null
  /** Open on the origin: the « Origine Winner » action of the line menu. */
  showOriginFirst?: boolean
  onSubmitted: () => void | Promise<void>
}

const toInput = (value: number | null | undefined) => (typeof value === 'number' ? String(value).replace('.', ',') : '')

/**
 * Edits a line that came from another system (a Winner export): it has no catalogue item to
 * render from, so its title, description, quantity, price and VAT are typed, as on a note. What
 * the file said stays beside it, unchanged, in « Origine Winner ».
 */
export function ImportedLineDialog({
  open,
  onOpenChange,
  projectId,
  quoteId,
  versionId,
  lineId,
  initialLine,
  origin,
  showOriginFirst = false,
  onSubmitted,
}: ImportedLineDialogProps) {
  const [vats, setVats] = useState<ProjectSalesVatsDto | null>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  // Null while the line has no properties: its description is then typed as it is.
  const [properties, setProperties] = useState<PropertyRow[] | null>(null)
  const [quantity, setQuantity] = useState('')
  const [unitPrice, setUnitPrice] = useState('')
  const [vatPercent, setVatPercent] = useState<number | null>(null)
  const [isOriginOpen, setIsOriginOpen] = useState(showOriginFirst)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open || !initialLine) return
    setTitle(initialLine.title ?? '')
    setDescription(initialLine.description ?? '')
    setProperties(initialLine.properties?.length ? propertyRows(initialLine.properties) : null)
    setQuantity(toInput(initialLine.quantity))
    setUnitPrice(toInput(initialLine.unitPrice))
    setVatPercent(typeof initialLine.vatPercent === 'number' ? initialLine.vatPercent : null)
    setIsOriginOpen(showOriginFirst)
    setError(null)
  }, [open, initialLine, showOriginFirst])

  useEffect(() => {
    if (!open) return
    let isMounted = true
    getProjectSalesVatsV2(projectId)
      .then((response) => {
        if (isMounted) setVats(response)
      })
      .catch(() => {
        if (isMounted) setError('Erreur lors du chargement des taux de TVA')
      })
    return () => {
      isMounted = false
    }
  }, [open, projectId])

  const parsedQuantity = parseDecimalInput(quantity)
  const parsedPrice = parseDecimalInput(unitPrice)
  const quantityIsWhole = isWholeQuantity(parsedQuantity)
  const propertiesError = properties ? propertiesProblem(properties) : null
  const canSubmit =
    !!quoteId && !!versionId && !!lineId && !!initialLine && title.trim() !== '' && quantityIsWhole
    && parsedPrice != null && vatPercent != null && propertiesError == null && !isSubmitting

  const handleSubmit = async () => {
    if (!canSubmit || !quoteId || !versionId || !lineId || !initialLine) return
    try {
      setIsSubmitting(true)
      setError(null)
      const trimmedDescription = description.trim()
      await updateQuoteVersionLineV2(projectId, quoteId, versionId, lineId, {
        kind: initialLine.kind,
        title: title.trim(),
        // With properties, the API composes the description from them.
        description: properties ? null : trimmedDescription ? trimmedDescription : null,
        properties: properties ? toLineProperties(properties) : null,
        familyId: null,
        familyKey: null,
        attributes: null,
        quantity: parsedQuantity,
        unitPrice: parsedPrice,
        vatPercent,
        amount: null,
      })
      await onSubmitted()
      onOpenChange(false)
    } catch (e) {
      // The API says which limit the properties break; anything else stays generic.
      setError(
        e instanceof ApiError && e.code === 'INVALID_PROPERTIES' ? e.message : "Erreur lors de l'enregistrement de la ligne",
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const summary = origin ? originSummary(origin) : ''

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[600px]" data-testid="imported-line-dialog">
        <DialogHeader>
          <DialogTitle>Modifier la ligne importée</DialogTitle>
          <DialogDescription>{summary || 'Ligne importée depuis Winner.'}</DialogDescription>
        </DialogHeader>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          {origin && (
            <Collapsible open={isOriginOpen} onOpenChange={setIsOriginOpen} className="rounded-md border bg-muted/30">
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md"
                  data-testid="imported-line-origin-toggle"
                >
                  <span>Origine Winner</span>
                  <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', isOriginOpen && 'rotate-180')} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <dl className="grid grid-cols-[88px_1fr] gap-x-3 gap-y-1.5 px-3 pb-3 text-xs" data-testid="imported-line-origin-panel">
                  {originFacts(origin).map((fact) => (
                    <div key={fact.label} className="contents">
                      <dt className="text-muted-foreground">{fact.label}</dt>
                      <dd className="whitespace-pre-line break-words">{fact.value}</dd>
                    </div>
                  ))}
                </dl>
                <p className="px-3 pb-3 text-[11px] text-muted-foreground">Ce que disait le fichier. Modifier la ligne ne le change pas.</p>
              </CollapsibleContent>
            </Collapsible>
          )}

          <div className="space-y-2">
            <Label htmlFor="imported-line-title">Titre</Label>
            <Input
              id="imported-line-title"
              data-testid="imported-line-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={512}
            />
          </div>

          {properties ? (
            <div className="space-y-2">
              <Label>Propriétés</Label>
              <p className="text-xs text-muted-foreground">
                Le détail imprimé sous la ligne est composé à partir des propriétés, dans cet ordre.
              </p>
              <LinePropertiesEditor rows={properties} onChange={setProperties} />
              {propertiesError && (
                <p role="alert" className="text-xs text-destructive" data-testid="imported-line-properties-error">
                  {propertiesError}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="imported-line-description">Description</Label>
              <Textarea
                id="imported-line-description"
                data-testid="imported-line-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                maxLength={4000}
              />
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto px-0"
                onClick={() => setProperties([...propertyRowsFromDescription(description), newPropertyRow()])}
                data-testid="imported-line-properties-start-btn"
              >
                Décrire par propriétés
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="imported-line-quantity">Quantité</Label>
              <DecimalInput
                id="imported-line-quantity"
                data-testid="imported-line-quantity-input"
                value={quantity}
                onValueChange={setQuantity}
              />
              {quantity !== '' && !quantityIsWhole && (
                <p role="alert" className="text-xs text-destructive" data-testid="imported-line-quantity-hint">
                  Un nombre entier de pièces : le devis et la facture n’impriment pas de décimales.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="imported-line-price">PU HT (€)</Label>
              <DecimalInput
                id="imported-line-price"
                data-testid="imported-line-price-input"
                value={unitPrice}
                onValueChange={setUnitPrice}
              />
            </div>
            <div className="space-y-2">
              <Label>TVA</Label>
              <Select
                value={vatPercent != null ? String(vatPercent) : undefined}
                onValueChange={(v) => setVatPercent(parseInt(v, 10))}
              >
                <SelectTrigger data-testid="imported-line-vat-select">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  {vatOptionsFor(vats?.availableVats ?? [], initialLine?.vatPercent, vatPercent).map((opt) => (
                    <SelectItem key={String(opt.value)} value={String(opt.value)}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Annuler
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={!canSubmit} data-testid="imported-line-save-btn">
            {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
